import React from "react";
import { Typography, Tooltip, Theme } from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import { useTranslate, Translate } from "react-admin";

export interface IArrayFieldProps {
  source: string;
  displayedElementsCount: number;
  label?: string;
  emptyArrayDisplay?: string;
  record?: any;
  displayMember?: keyof any;
  labelTranslationNamespace?: string;
  sortable?: boolean;
}

const appendToPreviousValue = (
  prev: string,
  newValue: any,
  displayMember: keyof any | undefined = undefined,
  translate: Translate,
  labelTranslationNamespace?: string
) => {
  let labelToTranslate: string;
  if (displayMember && displayMember in newValue) {
    labelToTranslate = newValue[displayMember];
  } else {
    labelToTranslate = newValue;
  }

  labelToTranslate = !!labelTranslationNamespace
    ? `${labelTranslationNamespace}.${labelToTranslate}`
    : labelToTranslate;
  return `${prev}, ${translate(labelToTranslate, { _: labelToTranslate })}`;
};

const setInitialValue = (
  newValue: any,
  displayMember: keyof any | undefined = undefined,
  translate: Translate,
  labelTranslationNamespace?: string
) => {
  let labelToTranslate: string;
  if (displayMember && displayMember in newValue) {
    labelToTranslate = newValue[displayMember];
  } else {
    labelToTranslate = newValue;
  }

  labelToTranslate = !!labelTranslationNamespace
    ? `${labelTranslationNamespace}.${labelToTranslate}`
    : labelToTranslate;
  return translate(labelToTranslate, { _: labelToTranslate });
};

const getDisplayValues = (
  elementsToDisplay: Array<any>,
  displayMember: keyof any | undefined = undefined,
  translate: Translate,
  labelTranslationNamespace?: string
) => {
  return elementsToDisplay.reduce((prev: string, element) => {
    if (!prev || prev.length === 0) {
      return setInitialValue(
        element,
        displayMember,
        translate,
        labelTranslationNamespace
      );
    } else {
      return appendToPreviousValue(
        prev,
        element,
        displayMember,
        translate,
        labelTranslationNamespace
      );
    }
  }, "");
};

export const ArrayField = (props: IArrayFieldProps) => {
  const theme: Theme = useTheme();
  const translate = useTranslate();

  const {
    source,
    record,
    displayedElementsCount,
    emptyArrayDisplay,
    displayMember,
    labelTranslationNamespace,
  } = props;
  if (!record) {
    return null;
  }

  if (
    !record[source] ||
    !Array.isArray(record[source]) ||
    (record[source] as Array<any>).filter((x) => !!x).length === 0
  ) {
    return emptyArrayDisplay ? (
      <Typography variant="body2">
        ({translate(emptyArrayDisplay, { _: emptyArrayDisplay })})
      </Typography>
    ) : null;
  }

  const array = record[source] as Array<any>;
  const elementsToDisplay = array.slice(0, displayedElementsCount);
  const restElements = array.slice(displayedElementsCount);
  const mainText = getDisplayValues(
    elementsToDisplay,
    displayMember,
    translate,
    labelTranslationNamespace
  );
  if (restElements.length === 0) {
    return (
      <Typography variant="body2">
        {translate(mainText, { _: mainText })}
      </Typography>
    );
  }

  return (
    <div className="array-field">
      <Typography variant="body2">{mainText}</Typography>
      <Tooltip
        title={getDisplayValues(
          restElements,
          displayMember,
          translate,
          labelTranslationNamespace
        )}>
        <Typography
          variant="caption"
          style={{
            color: theme.palette.info.main,
          }}>
          {translate("arrayField.countOthers", {
            smart_count: restElements.length,
          })}
        </Typography>
      </Tooltip>
    </div>
  );
};
